import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@nimbitlabs/ui/components/button';
import { Input } from '@nimbitlabs/ui/components/input';
import { Label } from '@nimbitlabs/ui/components/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@nimbitlabs/ui/components/card';
import { ErrorAlert } from '@nimbitlabs/ui';
import { Loader2, Building2, ArrowLeft } from 'lucide-react';

interface CreateAccountProps {
  user?: {
    email: string;
    name: string;
  };
  initialData?: {
    companyName?: string;
    phone?: string;
  };
  onLoadAccounts?: () => Promise<Array<{ companyName: string }>>;
  onCreateAccount: (data: { name: string; companyName: string; email: string; phone: string | null }) => Promise<{ id: string }>;
  onAccountCreated?: (accountId: string) => Promise<void>;
  backPath?: string;
  successPath?: string;
  description?: string;
}

export function CreateAccount({
  user,
  initialData,
  onLoadAccounts,
  onCreateAccount,
  onAccountCreated,
  backPath = '/',
  successPath = '/',
  description = "Let's get your workspace set up",
}: CreateAccountProps) {
  const [formData, setFormData] = useState({
    companyName: initialData?.companyName || '',
    phone: initialData?.phone || ''
  });
  const [error, setError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [existingAccounts, setExistingAccounts] = useState<string[]>([]);
  const navigate = useNavigate();

  // Load existing accounts to check for duplicates
  useEffect(() => {
    const loadAccounts = async () => {
      if (!onLoadAccounts) return;
      
      try {
        const accounts = await onLoadAccounts();
        if (accounts && Array.isArray(accounts)) {
          setExistingAccounts(accounts.map(a => a.companyName?.toLowerCase() || '').filter(Boolean));
        } else {
          setExistingAccounts([]);
        }
      } catch (err: any) {
        setExistingAccounts([]);
      }
    };
    loadAccounts();
  }, [onLoadAccounts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.companyName.trim()) {
      setError(new Error('Company name is required'));
      return;
    }

    if (!user?.email || !user?.name) {
      setError(new Error('User information not available. Please log in again.'));
      return;
    }

    // Check if company name already exists
    if (existingAccounts.includes(formData.companyName.trim().toLowerCase())) {
      setError(new Error('You already have access to an account with this company name. Please use a different name.'));
      return;
    }

    setIsLoading(true);

    try {
      const account = await onCreateAccount({
        name: user.name,
        companyName: formData.companyName,
        email: user.email,
        phone: formData.phone || null
      });

      // Call optional callback with new account ID
      if (onAccountCreated) {
        await onAccountCreated(account.id);
      }
      
      navigate(successPath);
    } catch (err: any) {
      console.error('Account creation error:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(backPath)}
            className="w-fit mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Accounts
          </Button>
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <CardTitle className="text-2xl text-center">Tell us about your company</CardTitle>
          <CardDescription className="text-center">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName">What's your company name? *</Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="e.g., Acme Inc."
                value={formData.companyName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (optional)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="e.g., +1 (555) 123-4567"
                value={formData.phone}
                onChange={handleInputChange}
              />
              <p className="text-xs text-muted-foreground">
                We'll use this for important account updates and support
              </p>
            </div>

            <ErrorAlert error={error} />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Setting up your workspace...' : 'Get Started'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>You'll be set up as the Admin for this workspace</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default CreateAccount;
